import { HCIP_VOLUMES, HCIP_COMBINED_URL, hcipUrl } from '../data/hcipSources'
import { formatYearRange } from '../utils/format'

interface Props {
  onClose: () => void
}

/**
 * HelpPage — the "how to use" / readme view. Rendered as a full-page
 * replacement for the main map+detail layout when urlState.view === 'help'.
 * Written in Bhavan voice (Sanskritic register, IAST), explanatory but not
 * stuffy. The "back" button below clears the view query param.
 */
export default function HelpPage({ onClose }: Props) {
  return (
    <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-8 relative z-[1]">
      {/* Sticky back affordance at the top so it's always reachable. */}
      <div className="mb-6">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded bg-parchment-200 hover:bg-saffron-50 hover:text-saffron-500 text-ink-100 border border-ink-300/8 transition-colors"
        >
          ← Back to the atlas
        </button>
      </div>

      <article className="prose-bhavan font-body text-ink-200 leading-relaxed text-[15px] space-y-8">

        <header>
          <h1 className="font-display iast text-4xl text-ink-300 leading-tight mb-2">How to use the Bhāvan Atlas</h1>
          <p className="text-ink-50 italic text-sm">
            A short guide to navigating an atlas of Indian history through the eyes
            of R. C. Majumdar's{' '}
            <a
              href={HCIP_COMBINED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="not-italic underline decoration-ink-300/30 underline-offset-2 hover:text-saffron-400 hover:decoration-saffron-400 transition-colors"
            >
              <em>History &amp; Culture of the Indian People</em>
            </a>
            {' '}(Bharatiya Vidya Bhavan, 11 volumes, 1951–1977).
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">What this is</h2>
          <p>
            The atlas connects two things the printed Bhavan volumes keep apart: <em>place</em> and
            <em> time</em>. You can begin with a region — say, Magadha or Gandhāra, or a modern name
            like Patna or Peshawar — and read what was happening there at any moment Bhavan's
            contributors record. Or you can begin with a moment — the year of the Bhārata war, the
            reign of Aśoka, the founding of Vijayanagara — and see what was happening across the
            whole of <em>Bhāratavarṣa</em> and its outer limbs.
          </p>
          <p>
            The voice you'll encounter on the right-hand panel is deliberately Bhavan's own:
            confident, civilisationally anchored, freely reaching for Sanskrit and Prākrit terms
            (<em>dharma</em>, <em>saṃghāta</em>, <em>cakravartin</em>) where these carry the meaning
            best. IAST diacritics are used throughout for proper nouns. Hover any italicised
            Sanskrit term for a brief gloss.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">The map</h2>
          <p>
            The map shows Greater India — the subcontinent proper together with the <em>limbs</em>
            that Bhavan's volumes return to repeatedly: Bactria, Gandhāra, Tibet, Sri Lanka, and
            the South-East Asian polities of Suvarṇabhūmi, Campā, Kambuja, Śrīvijaya, and
            Yavadvīpa.
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Click a region marker</strong> to focus on it. The right panel will show every
              event that touches that region.</li>
            <li><strong>Mouse wheel or trackpad pinch</strong> to zoom in or out, anchored at your
              cursor.</li>
            <li><strong>Click and drag</strong> to pan around.</li>
            <li>The <strong>+, −, and ⤺</strong> buttons in the top-right of the map zoom in, zoom
              out, and reset to the auto-fit view.</li>
            <li>When you pick an era or year, the map <strong>auto-fits</strong> to the regions in
              scope for that period — so the Vedic age view sits over Āryāvarta and the Indus,
              while the Cōḻa-era view will eventually extend out to Java and Sumatra.</li>
          </ul>
          <p className="text-sm text-ink-50 italic">
            India is drawn per the Survey of India boundary — the full pre-1947 Jammu &amp; Kashmir
            including Pakistan-occupied Kashmir, Gilgit-Baltistan, Aksai Chin, and the
            Trans-Karakoram Tract are shown as Indian territory. Neighbouring countries are drawn
            from Natural Earth at 1:50m.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">The era bar</h2>
          <p>
            Above the map sits a row of eleven cards, one per volume of HCIP, from the Vedic Age
            through to the Struggle for Freedom. Click any card to filter the atlas to that era.
            Click it again to clear the filter.
          </p>
          <p>
            Below the cards, a year slider runs from <em>−1500</em> to <em>1947</em>. Drag the
            saffron pill, or click anywhere along the bar, to fix on a single year. Events within
            a generation (~25 years) of that year will show up. The era your year falls into is
            highlighted in the band beneath.
          </p>
          <p>
            <strong>Picking a new era or year resets your map zoom</strong> so the new period's
            extent is in frame. Selecting a region alone never resets your zoom.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">Search</h2>
          <p>
            The search bar in the header accepts both <strong>places</strong> and <strong>historical
            figures</strong>, in either their classical or modern form. Try:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><em>Place:</em> "Magadha", "Pāṭaliputra", "Patna", "Rajagriha", "Bodh Gaya" — all
              find the same region.</li>
            <li><em>Figure:</em> "Krishna" finds Vāsudeva Kṛṣṇa; "Ashoka" finds Aśoka;
              "Yājñavalkya", "Maitreyī", "Janaka" all resolve to their Vedic-age entries.</li>
          </ul>
          <p>
            Picking a figure filters events to those involving them — useful for following one
            person's arc across regions.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">The event cards</h2>
          <p>
            Each event in the right-hand panel carries Bhavan's framing of the moment, with the
            original Sanskrit/Prākrit terminology preserved. The pills below an event card
            are <strong>clickable</strong>:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Region pills</strong> jump you to that region.</li>
            <li><strong>Figure pills</strong> jump you to that person's life.</li>
            <li><strong>Sanskrit term pills</strong> with a tooltip glossary; if the term names an
              entity the atlas knows about (e.g. "Kurukṣetra"), the pill is itself a link to
              that entity.</li>
            <li><strong>Source pills</strong> cite the HCIP volume, chapter, and page so you can
              cross-reference the original.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">Themes</h2>
          <p>
            Switch between <strong>Parchment</strong> (the default — a hand-drawn, sepia-toned
            atlas feel) and <strong>Broadsheet</strong> (a data-journalism look with white surfaces
            and a restrained dark-blue accent) using the picker in the footer. Both themes pull
            from the same data; only the surface treatment changes.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">Two chronologies, side by side</h2>
          <p>
            For watershed events in deep antiquity — most famously the Bhārata war — Bhavan's
            contributors and the broader scholarly tradition disagree. The atlas
            preserves <strong>both</strong>: Bhavan's Puranic dating (Manu at <em>−3100</em>,
            Yayāti at <em>−3000</em>, the Bhārata war at <em>−1400</em>) sits alongside the
            Pargiter / colonial-era dating (the war at <em>−950</em>) as separate events. You
            will see both timelines unless you filter to one.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">What's in the data today</h2>
          <p>
            The atlas is being built up volume by volume. As of this build:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>~129 events</strong> seeded from Volume 1 (the Vedic Age) plus baseline
              watersheds from the later volumes.</li>
            <li><strong>48 regions</strong>, including the Greater-India limbs.</li>
            <li><strong>30 dynasties</strong>, from the Haryaṅkas through the British Raj.</li>
            <li><strong>64 figures</strong> — 30 baseline (Buddha through Gandhi) plus 34 Vedic-age
              additions (Vasiṣṭha, Sudās, Janaka, Yājñavalkya, Maitreyī, Gārgī, and others).</li>
          </ul>
          <p>
            Volumes 2 through 11 — Imperial Unity, the Classical Age, the Sultanate, the Mughul
            empire, the Marāṭhā supremacy, the British paramountcy, and the Freedom struggle — are
            in the extraction queue. Target density is roughly 1500–3000 events when complete.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display iast text-2xl text-ink-300">Caveats</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>This is a research preview, not a finished work of reference. Treat dates and
              attributions as guides to Bhavan's framing, not as final scholarly verdicts.</li>
            <li>The voice is intentionally Bhavan's — <em>not</em> a neutral, present-day,
              consensus-academic register. If you want the latter, the secondary literature on
              every period in HCIP is vast.</li>
            <li>Mobile layout is provisional. The atlas is best viewed on a desktop browser at the
              moment.</li>
            <li>Some events may surface without map markers if their regions are tagged at a
              coarser level (e.g., "Āryāvarta", "the Deccan") rather than a single point.</li>
          </ul>
        </section>

        <section className="space-y-3 pt-4 border-t border-ink-300/10">
          <h2 className="font-display iast text-2xl text-ink-300">Browse the volumes</h2>
          <p>
            Every event card in this atlas carries a citation back to the relevant HCIP volume
            and chapter. The citations are clickable — each one opens that volume's public-domain
            scan on archive.org. The eleven volumes are also indexed in full below for direct
            browsing.
          </p>
          <ol className="space-y-1.5 list-none pl-0">
            {Object.values(HCIP_VOLUMES).map(v => (
              <li key={v.volume} className="flex items-baseline gap-3">
                <span className="text-ink-50 font-mono text-xs w-8 shrink-0">Vol&nbsp;{v.volume}</span>
                <a
                  href={hcipUrl(v.volume) ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-display iast text-ink-300 hover:text-saffron-400 underline decoration-ink-300/15 hover:decoration-saffron-400 underline-offset-2 transition-colors"
                >
                  {v.title}
                  <span aria-hidden="true" className="ml-1 text-[11px] opacity-60">↗</span>
                </a>
                <span className="text-ink-50 text-xs">
                  ({formatYearRange(v.startYear, v.endYear)})
                </span>
              </li>
            ))}
          </ol>
          <p className="text-sm text-ink-50 italic pt-2">
            All eleven volumes are also available as a combined low-resolution PDF on{' '}
            <a
              href={HCIP_COMBINED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="not-italic underline decoration-ink-300/30 hover:text-saffron-400 hover:decoration-saffron-400 underline-offset-2 transition-colors"
            >
              archive.org
            </a>
            {' '}for quick scanning.
          </p>
        </section>

        <section className="space-y-3 pt-4 border-t border-ink-300/10">
          <h2 className="font-display iast text-2xl text-ink-300">Acknowledgement</h2>
          <p>
            Every event card in this atlas is rooted in{' '}
            <a
              href={HCIP_COMBINED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-ink-300/30 hover:text-saffron-400 hover:decoration-saffron-400 underline-offset-2 transition-colors"
            >
              <em>History &amp; Culture of the Indian People</em>
            </a>, planned by K. M. Munshi, general-edited by R. C. Majumdar, and published by the
            Bharatiya Vidya Bhavan, Bombay, between 1951 and 1977. The atlas exists to help
            readers traverse that work spatially and chronologically; it does not replace it.
          </p>
        </section>

        <div className="pt-6">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded bg-saffron-50 text-saffron-500 hover:bg-saffron-100 border border-saffron-300/40 transition-colors"
          >
            ← Back to the atlas
          </button>
        </div>
      </article>
    </main>
  )
}
